import { ApolloClient } from 'apollo-client';
import { ApolloLink, split } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import { HttpLink } from 'apollo-link-http';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { WebSocketLink } from 'apollo-link-ws';
import { getMainDefinition } from 'apollo-utilities';
import { onError } from 'apollo-link-error';
import { RetryLink } from 'apollo-link-retry';

import { DEV_GRAPH_URL, NODE_ENV } from '../../env';
import { ERROR_CORS_403 } from '../../consts';
import { log } from '../../log';

const WINDOW_URL = new URL(DEV_GRAPH_URL || window.location.origin);

const clients = {};

let prioritizeCorsError = false; // Ensures we don't overwrite this specific error message with a non-descriptive network error message
let slowDownRetry = false; // used to slow down automatic retry on specific errors

const createGraphqlClient = (context) => {
  log.debug('[createGraphqlClient]');
  /**
   * Getting response headers
   * @see: https://stackoverflow.com/questions/54990173/graphql-cannot-read-response-headers-in-apollo-client
   */
  const authware = new ApolloLink((operation, forward) => forward(operation).map((response) => {
    const {
      response: { headers },
    } = operation.getContext();
    if (headers) {
      const env = headers.get('x-environment') || null;
      const apiVersion = headers.get('x-unraid-api-version') || null;
      if (env) context.commit('SET_MY_SERVERS_ENV', env);
      if (apiVersion) context.commit('SET_MY_SERVERS_API_VERSION', apiVersion);
    }
    return response;
  }));

  const httpLink = authware.concat(new HttpLink({ // HTTP connection to the API
    uri: `${WINDOW_URL.protocol}//${WINDOW_URL.host}/graphql`, // note the additional '//' slashes here for the URL
  }));
  // Create the subscription websocket link
  const wsProtocol = WINDOW_URL.protocol.includes('https') ? 'wss://' : 'ws://';
  const wsLinkUri = `${wsProtocol}${WINDOW_URL.host}/graphql`;
  const wsLink = new WebSocketLink({
    uri: wsLinkUri,
    options: {
      timeout: 65000,
      reconnect: true,
      connectionParams: {
        'x-api-key': context.state.apikey,
      },
    },
  });
  /**
   * Monitor websocket connection
   */
  let disconnectTimeout;
  wsLink.subscriptionClient.maxConnectTimeGenerator.setMin(3000);
  wsLink.subscriptionClient.onConnected(() => {
    log.debug('[🚀 wslink.onConnected 🚀]');
    context.commit('SET_GRAPH_ONLINE', true);
    if (context.state.myServersError) context.commit('SET_MY_SERVERS_ERROR', null);
  });
  wsLink.subscriptionClient.onReconnected(() => {
    log.debug('[🚀 wslink.onReconnected 🚀]');
    context.commit('SET_GRAPH_ONLINE', true);
    context.commit('SET_GRAPH_RECONNECTED', true);
    if (context.state.myServersError) context.commit('SET_MY_SERVERS_ERROR', null);
  });
  wsLink.subscriptionClient.onDisconnected(() => {
    log.debug('[🚀 wslink.onDisconnected 🚀] 01');
    if (context.state.graphOnline) { /** prevents below from firing on first load && when navigating away from the page */
      clearTimeout(disconnectTimeout);
      disconnectTimeout = setTimeout(() => { /** prevents below from firing when navigating away from the page */
        log.debug('[🚀 wslink.onDisconnected 🚀] 02');
        context.commit('SET_GRAPH_ONLINE', false);
        /** So we don't to overwrite "Graphql is offline" error message */
        if (!context.state.myServersError) context.commit('SET_MY_SERVERS_ERROR', 'Disconnected from Unraid API');
      }, 150);
    }
  });

  const link = split( // using the ability to split links, you can send data to each link depending on what kind of operation is being sent
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLink,
  );
  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      'x-api-key': context.state.apikey,
    },
  }));

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      log.error('[GraphQL error] %o', graphQLErrors);
      const msg = (graphQLErrors[0].message || graphQLErrors[0].error.message) || 'No error object - [UNRAID]';
      context.commit('SET_MY_SERVERS_ERROR', msg);
      if (msg && msg.includes(ERROR_CORS_403)) {
        prioritizeCorsError = true;
        slowDownRetry = true;
      }
    }
    if (networkError && !prioritizeCorsError) {
      log.error('[Network error]', networkError);
      const msg = networkError.message ? networkError.message : networkError;
      context.commit('SET_MY_SERVERS_ERROR', msg.includes('Unexpected token < in JSON at position 0') ? 'Unraid API • CORS Error' : msg);
    }
    context.commit('SET_MY_SERVERS_LOADING', false);
  });

  const retryLink = new RetryLink({
    attempts: {
      max: Infinity,
      retryIf: (error, _operation) => {
        log.debug('[retryLink - retryIf]', { error, _operation, prioritizeCorsError });
        context.commit('SET_GRAPH_ONLINE', false);
        return !!error && !prioritizeCorsError; // don't retry when ERROR_CORS_403
      },
    },
    delay: {
      initial: slowDownRetry ? 3000 : 300,
      max: Infinity,
      jitter: true,
    },
  });

  return new ApolloClient({
    link: ApolloLink.from([retryLink, errorLink, authLink, link]),
    connectToDevTools: NODE_ENV !== 'production',
    cache: new InMemoryCache(), // even though we aren't caching this is required because apollo…🙄 @see: https://github.com/apollographql/apollo-client/issues/6237
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
      },
      query: {
        fetchPolicy: 'no-cache',
      },
      mutate: {
        fetchPolicy: 'no-cache',
      },
      subscribe: {
        fetchPolicy: 'no-cache',
        shouldResubscribe: false,
      },
    },
  });
};

// eslint-disable-next-line import/prefer-default-export
export const getGraphqlClient = (context) => {
  if (!context.state.apikey) return log.debug('[getGraphqlClient] NO API KEY');
  if (Object.keys(clients).includes(context.state.apikey)) { // Return existing client
    log.debug('[getGraphqlClient] found existing client %o', clients[context.state.apikey]);
    return clients[context.state.apikey];
  }
  const client = createGraphqlClient(context);
  clients[context.state.apikey] = client;
  log.debug('[getGraphqlClient] new client inbound %o', client);
  return client;
};

export const stopGraphqlClient = (_context, apikey) => {
  log.debug('[stopGraphqlClient] clients %o', { clients, apikey });
  if (!Object.keys(clients).includes(apikey)) return log.debug('[stopGraphqlClient] client does not exist for apikey %o', apikey);
  log.debug('[stopGraphqlClient] found existing client & stopping it %o', clients[apikey]);
  return clients[apikey].stop();
};
